import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"

export interface DateInputProps {
    value?: string // YYYY-MM-DD
    placeholder?: string
    onChange: (date: string) => void
    disabled?: boolean
    className?: string
    id?: string
    /** YYYY-MM-DD permitidas. El resto queda deshabilitado en el calendario. */
    allowedDates?: string[]
    /** YYYY-MM-DD inclusive: días posteriores quedan deshabilitados. */
    max?: string
    onDisallowedDate?: (ymd: string) => void
    /** Necesario dentro de Dialog (Radix): el popover tiene que ser modal. */
    popoverModal?: boolean
}

export function DateInput({
    value,
    placeholder = "dd/mm/yyyy",
    onChange,
    disabled = false,
    className,
    id,
    allowedDates,
    max,
    onDisallowedDate,
    popoverModal = false,
}: DateInputProps) {
    // Initialize inputValue based on value prop
    const [inputValue, setInputValue] = React.useState("")
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined)
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    /** Evita que, al limpiar `value` mientras el usuario edita, el efecto borre el texto parcial. */
    const [focused, setFocused] = React.useState(false)

    const allowedSet = React.useMemo(
        () => (allowedDates ? new Set(allowedDates) : null),
        [allowedDates],
    )

    const allowedBounds = React.useMemo(() => {
        if (!allowedDates || allowedDates.length === 0) {
            return { start: undefined as Date | undefined, end: undefined as Date | undefined }
        }
        const sorted = [...allowedDates].sort()
        const start = parse(sorted[0], "yyyy-MM-dd", new Date())
        const end = parse(sorted[sorted.length - 1], "yyyy-MM-dd", new Date())
        return {
            start: isValid(start) ? start : undefined,
            end: isValid(end) ? end : undefined,
        }
    }, [allowedDates])

    const maxDate = React.useMemo(() => {
        if (!max || !/^\d{4}-\d{2}-\d{2}$/.test(max)) return undefined
        const parsed = parse(max, "yyyy-MM-dd", new Date())
        return isValid(parsed) ? parsed : undefined
    }, [max])

    const isAllowedYmd = React.useCallback(
        (ymd: string) => {
            if (allowedSet && !allowedSet.has(ymd)) return false
            if (max && ymd > max) return false
            return true
        },
        [allowedSet, max],
    )

    const calendarEndMonth = React.useMemo(() => {
        const allowedEnd = allowedBounds.end
        if (allowedEnd && maxDate) return allowedEnd < maxDate ? allowedEnd : maxDate
        return allowedEnd ?? maxDate
    }, [allowedBounds.end, maxDate])

    const restoreFromValue = React.useCallback(() => {
        if (value) {
            const date = parse(value, "yyyy-MM-dd", new Date())
            if (isValid(date)) {
                setSelectedDate(date)
                setInputValue(format(date, "dd/MM/yyyy"))
                return
            }
        }
        setSelectedDate(undefined)
        setInputValue("")
    }, [value])

    React.useEffect(() => {
        if (value) {
            const date = parse(value, "yyyy-MM-dd", new Date())
            if (isValid(date)) {
                setSelectedDate(date)
                setInputValue(format(date, "dd/MM/yyyy"))
            }
        } else {
            if (!focused) {
                setSelectedDate(undefined)
                setInputValue("")
            }
        }
    }, [value, focused])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value

        // Auto-formatting logic: insert slashes
        // Remove non-digits and slashes
        const cleanInput = input.replace(/[^0-9/]/g, "")
        // Logic to ensure only 1 slash between valid parts? 
        // Or just simple mask: If user types "23012026", convert to "23/01/2026"

        setInputValue(cleanInput) // Allow typing

        // Attempt to parse strictly
        // formats like dd/MM/yyyy
        const parsedDate = parse(cleanInput, "dd/MM/yyyy", new Date())

        if (isValid(parsedDate) && cleanInput.length === 10) {
            const ymd = format(parsedDate, "yyyy-MM-dd")
            if (!isAllowedYmd(ymd)) {
                onDisallowedDate?.(ymd)
                return
            }
            setSelectedDate(parsedDate)
            onChange(ymd)
        } else if (!allowedSet && !max) {
            if (cleanInput === "") {
                onChange("")
            } else {
                // Incompleto o inválido: no conservar YYYY-MM-DD viejo en el padre (p. ej. Network / filtros).
                onChange("")
            }
        }
    }

    const handleBlur = () => {
        const trimmed = inputValue.trim()
        let invalid = false
        if (!trimmed) {
            invalid = true
        } else if (trimmed.length !== 10) {
            invalid = true
        } else {
            const parsedDate = parse(trimmed, "dd/MM/yyyy", new Date())
            if (!isValid(parsedDate)) invalid = true
        }
        if (!invalid && trimmed.length === 10) {
            const parsedDate = parse(trimmed, "dd/MM/yyyy", new Date())
            if (isValid(parsedDate) && !isAllowedYmd(format(parsedDate, "yyyy-MM-dd"))) {
                invalid = true
            }
        }
        if (invalid) {
            if (!trimmed) {
                onChange("")
                setSelectedDate(undefined)
                setInputValue("")
            } else if ((allowedSet || max) && value) {
                restoreFromValue()
            } else {
                onChange("")
                setSelectedDate(undefined)
                setInputValue("")
            }
        }
        setFocused(false)
    }

    const handleDaySelect = (date: Date | undefined) => {
        if (date) {
            const ymd = format(date, "yyyy-MM-dd")
            if (!isAllowedYmd(ymd)) {
                onDisallowedDate?.(ymd)
                return
            }
            setSelectedDate(date)
            setInputValue(format(date, "dd/MM/yyyy"))
            onChange(ymd)
            setIsPopoverOpen(false)
        } else {
            // Deselect
        }
    }

    return (
        <div className={cn("relative flex items-center", className)}>
            <Input
                id={id}
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setFocused(true)}
                onBlur={handleBlur}
                disabled={disabled}
                className="pr-10" // Make room for icon
                maxLength={10}
            />
            <Popover modal={popoverModal} open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant={"ghost"}
                        className={cn(
                            "absolute right-0 h-full w-10 p-0 text-muted-foreground hover:bg-transparent",
                            !value && "text-muted-foreground"
                        )}
                        disabled={disabled}
                    >
                        <CalendarIcon className="h-4 w-4" />
                        <span className="sr-only">Abrir calendario</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="z-[100] w-auto p-0" align="end">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDaySelect}
                        initialFocus
                        disabled={
                            allowedSet || max
                                ? (date) => !isAllowedYmd(format(date, "yyyy-MM-dd"))
                                : undefined
                        }
                        startMonth={allowedBounds.start}
                        endMonth={calendarEndMonth}
                        defaultMonth={
                            value && isValid(parse(value, "yyyy-MM-dd", new Date()))
                                ? parse(value, "yyyy-MM-dd", new Date())
                                : selectedDate ?? allowedBounds.start ?? maxDate
                        }
                    />
                </PopoverContent>
            </Popover>
        </div>
    )
}
